// pkg77 — D-77b billing receipt on every draft result: billed → "$X billed to {client} — draft archived to their Billing tab"; unbilled → "firm paid"; the archive mention reflects D-77c (billed drafts are snapshotted with the expense; firm-paid drafts stay copy-out-only) — delivered 2026-07-13
// pkg75 — Draft Correspondence page (D-75a/d/e + cost recovery): client context loads automatically; structured recipient/tone pickers; 1-credit draft with pkg65 language; editable output + Copy (draft-only — LawMighty never sends, D-75d); "Bill to client as an expense · $1.00" opt-in, firm pays by default — delivered 2026-07-13
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { adminApi } from '../utils/adminApi';

const font = { fontFamily: 'Arial, sans-serif' };

const RECIPIENTS = [
  { key: 'client',           label: 'My client' },
  { key: 'opposing_counsel', label: 'Opposing counsel' },
  { key: 'court_clerk',      label: 'Court clerk' },
  { key: 'other',            label: 'Other third party' },
];
const TONES = [
  { key: 'formal', label: 'Formal' },
  { key: 'plain',  label: 'Plain language' },
];

export default function AdminClientCorrespondencePage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [client,  setClient]  = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');
  const [notice,  setNotice]  = useState('');

  const [recipient,    setRecipient]    = useState('opposing_counsel');
  const [tone,         setTone]         = useState('formal');
  const [instructions, setInstructions] = useState('');
  const [billClient,   setBillClient]   = useState(false);
  const [billAmount,   setBillAmount]   = useState('1.00');

  const [drafting, setDrafting] = useState(false);
  const [draft,    setDraft]    = useState('');
  const [copied,   setCopied]   = useState(false);

  useEffect(() => {
    adminApi.getClient(id)
      .then(c => { setClient(c); setLoading(false); })
      .catch(err => { setError(err.message); setLoading(false); });
  }, [id]);

  async function handleDraft() {
    setDrafting(true); setError(''); setNotice(''); setCopied(false);
    try {
      const body = { recipient_type: recipient, tone, instructions };
      if (billClient) {
        body.bill_to_client = {
          enabled: true,
          amount_cents: Math.round(parseFloat(billAmount || '1') * 100),
        };
      }
      const r = await adminApi.draftClientCorrespondence(id, body);
      setDraft(r.draft || '');
      // pkg77 (D-77b) — every run's result carries a billing receipt.
      if (r.bill_requested && r.billed) {
        setNotice(`Draft ready. Billed $${r.billed.amount.toFixed(2)} to ${client?.name || 'the client'} as an expense — this draft is archived to their Billing tab.`);
      } else if (r.bill_requested && !r.billed) {
        setNotice('Draft ready, but the client charge did not record — add the expense manually from the Billing tab.');
      } else {
        setNotice('Draft ready — firm paid, nothing billed to the client. Review and edit below, then copy it out.');
      }
    } catch (err) { setError(err.message); }
    finally { setDrafting(false); }
  }

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(draft);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch (_) {
      setError('Copy failed — select the text and copy manually.');
    }
  }

  if (loading) return <div style={{ padding: 40, ...font, color: '#888' }}>Loading...</div>;
  if (!client) return (
    <div style={{ padding: 40 }}>
      {error && <div className="alert alert-error">{error}</div>}
      <button className="btn btn-sm" onClick={() => navigate('/admin/clients')}>← All Clients</button>
    </div>
  );

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 8 }}>
        <button className="btn btn-sm" onClick={() => navigate(`/admin/clients/${id}`)}>← {client.name}</button>
      </div>
      <h1 className="page-title">Draft Correspondence</h1>
      <p className="page-subtitle">
        AI-drafted letters about <strong>{client.name}</strong>
        {client.case_type && <> · {client.case_type}</>}. Drafts only — review, edit, and send
        through your own letterhead and delivery. Each draft uses 1 AI credit, only if it succeeds.
      </p>

      {error  && <div className="alert alert-error">{error}</div>}
      {notice && <div className="alert alert-success">{notice}</div>}

      <div className="card">
        <div className="form-row">
          <div className="form-group">
            <label>Recipient</label>
            <select value={recipient} onChange={e => setRecipient(e.target.value)}>
              {RECIPIENTS.map(r => <option key={r.key} value={r.key}>{r.label}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>Tone</label>
            <select value={tone} onChange={e => setTone(e.target.value)}>
              {TONES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
          </div>
        </div>
        <div className="form-group">
          <label>Purpose and key points</label>
          <textarea rows={4} value={instructions} onChange={e => setInstructions(e.target.value)}
            placeholder="What should this letter accomplish? Include the key facts, dates, requests, and any deadlines."
            style={{ width: '100%', ...font, fontSize: 13 }} />
        </div>
        <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
          <button className="btn btn-primary" disabled={drafting || !instructions.trim()} onClick={handleDraft}>
            {drafting ? <><span className="spinner" />Drafting…</> : '✦ Draft letter · 1 credit'}
          </button>
          {/* pkg75 cost recovery — firm pays unless opted in */}
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, ...font, fontSize: 13, cursor: 'pointer' }}
            title="Adds a billable expense to this client — it will appear on their next invoice. Leave unchecked and the firm absorbs the cost.">
            <input type="checkbox" checked={billClient} onChange={e => setBillClient(e.target.checked)} />
            Bill to {client.name} as an expense · $
            <input type="text" value={billAmount} disabled={!billClient}
              onChange={e => setBillAmount(e.target.value.replace(/[^0-9.]/g, ''))}
              style={{ width: 52, padding: '3px 6px', fontSize: 13, ...font, border: '1px solid #ccc', borderRadius: 4 }} />
          </label>
          <span style={{ ...font, fontSize: 12, color: '#888' }}>If drafting errors, no credit is used and nothing is billed.</span>
        </div>
      </div>

      {draft && (
        <>
          <div className="section-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <span>Draft — editable before you copy it out</span>
            <button className="btn btn-sm" onClick={handleCopy}>{copied ? '✓ Copied' : 'Copy'}</button>
          </div>
          <div className="card">
            <textarea rows={18} value={draft} onChange={e => setDraft(e.target.value)}
              style={{ width: '100%', ...font, fontSize: 13, lineHeight: 1.55 }} />
            <p style={{ ...font, fontSize: 11, color: '#999', margin: '10px 0 0' }}>
              AI-drafted — review before use. Not sent by LawMighty; copy into your own letterhead and delivery.
            </p>
          </div>
        </>
      )}
    </div>
  );
}
